"use client";

import type { LucideIcon } from "lucide-react";
import { Check } from "lucide-react";
import { Workspace } from "@/lib/api";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { DropdownMenuItem } from "@/app/components/ui/DropdownMenu";
import {
    DEFAULT_WORKSPACE_AVATAR,
    DEFAULT_WORKSPACE_ICON_SURFACE,
    resolveWorkspaceAvatarFromWorkspace,
    resolveWorkspaceImageDisplayUrl,
    WORKSPACE_AVATAR_COLORS,
} from "@/lib/workspaceAvatar";
import { workspaceRoleLabelTh } from "@/lib/workspaceRoleLabelsTh";

export const WORKSPACE_SELECT_AVATAR_SIZE = 24;
export const WORKSPACE_SELECT_ICON_SIZE = 16;

export const WORKSPACE_SELECT_ROW_CLASS =
    "min-h-0 cursor-pointer gap-2 rounded-[10px] px-2.5! py-2! text-sm font-medium text-aera-gray hover:bg-aera-offwhite hover:text-aera-charcoal focus:bg-aera-offwhite";

export const WORKSPACE_SELECT_ACTION_ROW_CLASS =
    "min-h-0 cursor-pointer gap-1.5 rounded-[10px] px-2.5! py-2! text-sm font-medium text-aera-gray hover:bg-aera-offwhite hover:text-aera-charcoal focus:bg-aera-offwhite";

function resolveWorkspaceDotColor(workspace: Workspace, index: number): string {
    const avatar = resolveWorkspaceAvatarFromWorkspace({
        imageUrl: workspace.imageUrl,
        tags: workspace.tags,
        resolveImageUrl: resolveWorkspaceImageDisplayUrl,
    });

    if (avatar.mode === "icon") {
        return avatar.color;
    }

    return WORKSPACE_AVATAR_COLORS[index % WORKSPACE_AVATAR_COLORS.length]?.value
        ?? DEFAULT_WORKSPACE_AVATAR.color;
}

export function WorkspaceSelectActionIcon({
    icon: Icon,
    className = "",
}: {
    icon: LucideIcon;
    className?: string;
}) {
    return (
        <div
            className={`flex shrink-0 items-center justify-center rounded-md ${className}`}
            style={{
                width: WORKSPACE_SELECT_AVATAR_SIZE,
                height: WORKSPACE_SELECT_AVATAR_SIZE,
                backgroundColor: DEFAULT_WORKSPACE_ICON_SURFACE,
            }}
        >
            <Icon
                size={WORKSPACE_SELECT_ICON_SIZE}
                className="text-aera-charcoal"
                strokeWidth={2}
            />
        </div>
    );
}

export type WorkspaceSelectListProps = {
    workspaces: Workspace[];
    selectedWorkspaceId?: string | null;
    onSelect: (workspaceId: string) => void;
    isLoading?: boolean;
    emptyMessage?: string;
    className?: string;
    disabled?: boolean;
    /** Use Radix menu items so clicks register inside DropdownMenu. */
    asMenu?: boolean;
};

function WorkspaceSelectListSkeleton({ rows = 4 }: { rows?: number }) {
    return (
        <div className="flex flex-col gap-0.5">
            {Array.from({ length: rows }).map((_, i) => (
                <div
                    key={i}
                    className="flex items-center gap-2.5 rounded-[10px] px-2.5 py-2 animate-pulse"
                >
                    <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-aera-lightgray" />
                    <div className="h-4 flex-1 max-w-[60%] rounded bg-aera-lightgray" />
                </div>
            ))}
        </div>
    );
}

function WorkspaceSelectRowContent({
    name,
    isActive,
    dotColor,
    access,
    role,
}: {
    name: string;
    isActive: boolean;
    dotColor: string;
    access?: "owned" | "member";
    role?: string | null;
}) {
    return (
        <>
            <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: dotColor }}
                aria-hidden
            />
            <span className="min-w-0 flex-1 text-left truncate">{name}</span>
            {access === "member" ? (
                <span className="shrink-0 rounded bg-aera-offwhite px-1.5 py-0.5 text-[10px] font-medium text-aera-gray">
                    {workspaceRoleLabelTh(role)}
                </span>
            ) : null}
            {isActive ? (
                <Check
                    size={16}
                    strokeWidth={3}
                    className="shrink-0 text-aera-amber"
                    aria-hidden
                />
            ) : (
                <span className="h-4 w-4 shrink-0" aria-hidden />
            )}
        </>
    );
}

export function WorkspaceSelectList({
    workspaces,
    selectedWorkspaceId,
    onSelect,
    isLoading = false,
    emptyMessage = "ยังไม่มี Workspace",
    className = "",
    disabled = false,
    asMenu = false,
}: WorkspaceSelectListProps) {
    if (isLoading && workspaces.length === 0) {
        return (
            <div className={className}>
                <WorkspaceSelectListSkeleton />
            </div>
        );
    }

    if (workspaces.length === 0) {
        return (
            <p className={`px-2 py-3 text-sm text-aera-gray ${className}`}>
                {emptyMessage}
            </p>
        );
    }

    return (
        <div
            className={`flex flex-col gap-0.5 ${disabled ? "pointer-events-none opacity-60" : ""} ${className}`}
        >
            {workspaces.map((workspace, index) => {
                const isActive = selectedWorkspaceId === workspace.id;
                const dotColor = resolveWorkspaceDotColor(workspace, index);
                const rowClass = `flex w-full min-h-0 cursor-pointer items-center justify-start gap-2.5 rounded-[10px] px-2.5 py-2 text-sm hover:bg-aera-offwhite ${
                    isActive
                        ? "font-bold text-aera-charcoal"
                        : "font-semibold text-aera-slate hover:text-aera-charcoal"
                }`;

                if (asMenu) {
                    return (
                        <DropdownMenuItem
                            key={workspace.id}
                            disabled={disabled}
                            className={rowClass}
                            onSelect={() => onSelect(workspace.id)}
                            aria-current={isActive ? "true" : undefined}
                        >
                            <WorkspaceSelectRowContent
                                name={workspace.name}
                                isActive={isActive}
                                dotColor={dotColor}
                                access={workspace.access}
                                role={workspace.role}
                            />
                        </DropdownMenuItem>
                    );
                }

                return (
                    <AeraButton
                        key={workspace.id}
                        variant="ghost"
                        fullWidth
                        type="button"
                        disabled={disabled}
                        paddingSize="xxs"
                        onClick={() => onSelect(workspace.id)}
                        className={`${rowClass} px-2.5! py-2!`}
                        aria-current={isActive ? "true" : undefined}
                    >
                        <WorkspaceSelectRowContent
                            name={workspace.name}
                            isActive={isActive}
                            dotColor={dotColor}
                            access={workspace.access}
                            role={workspace.role}
                        />
                    </AeraButton>
                );
            })}
        </div>
    );
}
